/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box, ButtonBase, Portal } from '@mui/material';
import { useUserPreferences } from '../../desktop/preferences/UserPreferencesContext';
import { useAgentModifierHeld } from '../components/hooks/useAgentModifierHeld';
import EdAvatar from '../EdAvatar';
import { formatAgentAriaShortcut, formatAgentShortcut } from '../keyboard';
import { useAgentLauncher } from './AgentLauncherContext';
import LauncherShortcutHint from './LauncherShortcutHint';
import LauncherWorkingOutline from './LauncherWorkingOutline';
import { getLauncherChipSx } from './launcherChipSx';
import { type AgentLauncherStatus, LAUNCHER_LABEL } from './launcherStatus';
import { type LauncherPosition, useLauncherChipDrag } from './useLauncherChipDrag';

function LauncherChip({
  position,
  status,
  onOpen,
  modifierHeld,
}: Readonly<{
  position: LauncherPosition;
  status: AgentLauncherStatus;
  onOpen: () => void;
  modifierHeld: boolean;
}>) {
  const { setPreference } = useUserPreferences();
  const { isDragging, handlers } = useLauncherChipDrag({
    onClick: onOpen,
    onDrop: (next) => setPreference('agent.launcherPosition', next),
  });
  const shortcut = formatAgentShortcut('A');
  const floating = position === 'bottom-right';

  const chip = (
    <ButtonBase
      aria-label={`Open PA Agent (${LAUNCHER_LABEL[status]})`}
      aria-keyshortcuts={formatAgentAriaShortcut('A')}
      data-position={position}
      data-status={status}
      data-testid="agent-launcher-chip"
      {...handlers}
      // Pointer handlers own click and drag; the keyboard still activates it.
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      sx={(theme) => getLauncherChipSx(theme, { floating, isDragging, status, modifierHeld })}
    >
      <Box component="span" sx={{ position: 'relative', width: 29, height: 30, flexShrink: 0 }}>
        <Box component="span" sx={{ position: 'absolute', left: '4px', top: '3.5px' }}>
          <EdAvatar size={22} />
        </Box>
        {status === 'new_reply' && (
          <Box
            component="span"
            data-testid="agent-launcher-reply-badge"
            sx={{
              position: 'absolute',
              left: '19px',
              top: '5px',
              width: 8,
              height: 8,
              boxSizing: 'content-box',
              borderRadius: '50%',
              bgcolor: 'primary.main',
              border: '1.5px solid',
              borderColor: 'background.paper',
              transform: 'translate(-1.5px, -1.5px)',
            }}
          />
        )}
      </Box>
      {status === 'working' && <LauncherWorkingOutline />}
      {LAUNCHER_LABEL[status]}
      <LauncherShortcutHint label={shortcut} active={modifierHeld} />
    </ButtonBase>
  );
  return floating ? <Portal>{chip}</Portal> : chip;
}

/**
 * The Agent launcher with the Ed gradient ring and modifier-held shortcut hint. Each place it can sit renders this
 * with its position; only the one matching Settings › Agent › Agent chip shows it. Click (or
 * ⌃A) opens the Agent; dragging it snaps it to the nearest position. Hidden while the Agent is
 * open.
 */
function AgentLauncherChip({ position }: Readonly<{ position: LauncherPosition }>) {
  const launcher = useAgentLauncher();
  const modifierHeld = useAgentModifierHeld();
  const { launcherPosition } = useUserPreferences().preferences.agent;

  if (!launcher || launcher.hidden || launcherPosition !== position) return null;
  return (
    <LauncherChip
      position={position}
      status={launcher.status}
      onOpen={launcher.onOpen}
      modifierHeld={modifierHeld}
    />
  );
}

export default AgentLauncherChip;
